<template>
    <div class="mt-4">
        <SkeletonLoader v-if="isLoading" :skeleton-data="skeletonData"/>
        <template v-else-if="currentDocument.pdf">
            <h4 class="text-center">{{ currentDocument.name }}</h4>
            <DocumentTestPreview v-if="hasStartedPreview" @closePreview="closePreview"/>
            <DocumentTest v-else-if="hasStartedTest" @retry="resetDocumentView"/>
            <template v-else>
                <div class="row">
                    <div class="col-sm-12 mt-4" v-if="totalPages > 1">
                        <div class="text-center">
                            <button @click="switchPage()" class="ds-btn ds-btn--default mr-3" :class="{'ds-btn--disabled': currentPage === 1}">
                                Iepriekšējā lapa
                            </button>
                            <button @click="switchPage(true)" class="ds-btn ds-btn--default">Nākamā lapa</button>
                        </div>
                        <b-progress class="ds-progress mt-2" :max="totalPages" show-value>
                            <b-progress-bar :value="currentPage" variant="success"/>
                        </b-progress>
                    </div>
                    <div class="offset-sm-1 col-sm-10 my-4 border shadow-sm position-relative"
                         style="height: 700px; overflow-y: scroll;">
                        <pdf :src="currentDocument.pdf"
                             :page="currentPage"
                             @num-pages="handleInitialPdfLoad"
                             @page-loaded="isPageLoading = false"/>

                        <div v-if="isPageLoading" class="d-flex align-items-center justify-content-center">
                            <font-awesome-icon v-if="isPageLoading" icon="layer-group" size="5x"/>
                        </div>
                    </div>
                </div>

                <template v-if="currentDocument.video">
                    <div class="row">
                        <div class="offset-sm-1 col-sm-10 mb-5">
                            <b-button class="ds-btn ds-btn--default" block href="#" v-b-toggle.accordion-1 variant="info">
                                Video <font-awesome-icon icon="video"/>
                            </b-button>
                            <b-collapse id="accordion-1" accordion="my-accordion" role="tabpanel">
                                <b-card  class="p-5">
                                    <video style="width: 100%; max-height: 100%;" controls>
                                        <source :src="currentDocument.video">
                                    </video>
                                </b-card>
                            </b-collapse>
                        </div>
                    </div>
                </template>

                <div class="row" v-if="currentDocument.isRequired && currentUser.isUser">
                    <div v-if="currentDocument.isPassed" class="offset-sm-1 col-sm-8 mb-5">
                        <font-awesome-icon icon="check-circle" class="ds-btn--icon-green"/> Esi veiksmīgi nokārtojis!
                    </div>
                    <div v-else class="offset-sm-1 col-sm-8 px-0">
                        <button v-if="isCustom(currentDocument.documentForm.type)"
                                @click="hasStartedTest = true"
                                class="ds-btn ds-btn--default"
                                :class="{'ds-btn--disabled': !hasReadDocuments}"
                                :disabled="!hasReadDocuments || isActiveRequest">
                            Sākt testu
                        </button>
                        <button v-else
                                @click="finishTest({documentId: $route.params.documentId})"
                                class="ds-btn ds-btn--default mb-4"
                                :class="{'ds-btn--disabled': !hasReadDocuments}"
                                :disabled="!hasReadDocuments || isActiveRequest">
                            Esmu iepazinies
                        </button>
                    </div>
                </div>
                <div class="row" v-else-if="currentUser.isManager 
                                        && isCustom(currentDocument.documentForm.type)">
                    <div class="offset-sm-1 col-sm-8 px-0">
                        <button class="ds-btn ds-btn-default"
                            @click="hasStartedPreview = true">Testa priekšskatījums</button>
                    </div>
                </div>
            </template>
        </template>
        <template v-else-if="!currentDocument.pdf">
            <h4 class="text-center">{{ currentDocument.name }}</h4>
            <DocumentTestPreview v-if="currentUser.isManager" @closePreview="closePreview"/>
            <DocumentTest v-else @retry="resetDocumentView"/>
            
        </template>
    </div>
</template>
<script>
    import {mapActions, mapGetters} from 'vuex';
    import pdf from 'vue-pdf';

    import * as documentStoreTypes from '../../vuex/modules/document/document.store-types';
    import {DocumentForm} from '@/vuex/modules/document/models/document-form.models';
    import DocumentTest from '@/components/documents/DocumentTest';
    import DocumentTestPreview from '@/components/documents/DocumentTestPreview';
    import * as authStoreTypes from "../../vuex/modules/auth/auth.store-types";
    import SkeletonLoader from '@/components/global/skeleton-loader/SkeletonLoader';
    import {SkeletonLoaderRepository} from '@/components/global/skeleton-loader/SkeletonLoaderRepository';

    export default {
        name: 'single-document',
        components: {SkeletonLoader, DocumentTest, DocumentTestPreview, pdf},
        data: () => ({
            pdfDocumentInstance: {},
            hasReadDocuments: false,
            hasStartedTest: false,
            hasStartedPreview: false,
            currentPage: 1,
            totalPages: 0,
            isPageLoading: true,
            skeletonData: SkeletonLoaderRepository.documentTestLayout,
        }),
        async created() {
            const currentDocument = await this.getDocument({documentId: this.$route.params.documentId});

            if (!currentDocument._id) {
                this.$router.push({name: 'index'});
            }

            try {
                this.pdfDocumentInstance = await pdf.createLoadingTask(this.currentDocument.pdf).promise;
            } catch (err) {
                //
            }
        },
        methods: {
            ...mapActions('document', {
                /** @type {function|*} */
                getDocument: documentStoreTypes.ACTION_GET_DOCUMENT,
                /** @type {function|*} */
                finishTest: documentStoreTypes.ACTION_FINISH_TEST,
            }),
            /** @type {?boolean} isNext */
            switchPage(isNext = false) {
                if (isNext && this.totalPages === this.currentPage || !isNext && this.currentPage - 1 < 1) {
                    return;
                }

                this.isPageLoading = true;

                this.currentPage = isNext ? this.currentPage + 1 : this.currentPage - 1;

                this.hasReadDocuments = this.currentPage === this.totalPages;
            },
            handleInitialPdfLoad(numPages) {
                this.totalPages = numPages;
                this.hasReadDocuments = this.totalPages === this.currentPage;
            },
            isCustom: type => DocumentForm.isCustom(type),
            resetDocumentView() {
                this.hasStartedTest = false;
            },
            closePreview() {
                this.hasStartedPreview = false;
            }
        },
        computed: {
            ...mapGetters('document', {
                /** @type {Document|*} */
                currentDocument: documentStoreTypes.GET_CURRENT_DOCUMENT,
                /** @type {boolean|*} */
                isLoading: documentStoreTypes.GET_IS_LOADING,
                /** @type {boolean|*} */
                isActiveRequest: documentStoreTypes.GET_IS_ACTIVE_REQUEST,
            }),
            ...mapGetters('auth', {
                /** @type {CurrentUser|*} */
                currentUser: authStoreTypes.GET_CURRENT_USER,
            }),
        }
    }
</script>